'use client'

import useValuation, { type Valuation } from './useValuation'

export interface BidReference {
	/** The price a bid anchors to: the fair-value mark, or the floor while no mark exists. */
	sats: number | null
	/** What the app calls this number: the fair-value mark is the floor price; a bare listing floor stands in when no mark exists. */
	label: 'Floor price'
	fairSats: number | null
	floorSats: number | null
	valuation?: Valuation
}

/** Pure half, for tests and server code: fair value first, floor as the fallback. */
export function bidReferenceFrom(
	valuation: Valuation | undefined,
	floorSats?: number | null
): BidReference {
	const fair = valuation?.fair_sats
	const fairSats = fair != null && Number.isFinite(fair) && fair > 0 ? Math.round(fair) : null
	const floor =
		floorSats != null && Number.isFinite(floorSats) && floorSats > 0
			? Math.round(floorSats)
			: valuation?.inputs?.floor_sats && valuation.inputs.floor_sats > 0
			? Math.round(valuation.inputs.floor_sats)
			: null
	return fairSats
		? { sats: fairSats, label: 'Floor price', fairSats, floorSats: floor, valuation }
		: { sats: floor, label: 'Floor price', fairSats: null, floorSats: floor, valuation }
}

/**
 * What a new bid should be measured against. The floor is the cheapest live
 * ask and the easiest number on the marketplace to move; the fair-value mark
 * sits between the best bid and the floor, weighted by recent sales, so bids
 * anchored to it start where the market actually clears.
 */
export default function useBidReference(
	slug: string | null | undefined,
	floorSats?: number | null
): BidReference {
	const { valuation } = useValuation(slug || null)
	return bidReferenceFrom(valuation, floorSats)
}
